import React, { useState, useEffect } from "react";

import {
  FaImages,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import AOS from "aos";
import "aos/dist/aos.css";

import "./Gallery.css";
import { BASE_URL } from "../../Config/config";

export default function Gallery() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ============================================================
  // PAGINATION
  // ============================================================

  const [currentPage, setCurrentPage] = useState(1);

  // Number of images displayed on each page
  const itemsPerPage = 30;

  // Total number of pages
  const totalPages = Math.ceil(galleryData.length / itemsPerPage);

  // Starting index for current page
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Images displayed on current page
  const currentGalleryData = galleryData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // ============================================================
  // FETCH GALLERY
  // ============================================================

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);

        const url = `${BASE_URL}/gallery.php?action=get`;

        console.log("Gallery API URL:", url);

        const response = await fetch(url);

        console.log("Gallery HTTP Status:", response.status);

        const text = await response.text();

        console.log("Gallery Raw Response:", text);

        let result;

        try {
          result = JSON.parse(text);
        } catch (error) {
          console.error("Gallery API returned invalid JSON:", text);
          return;
        }

        console.log("Gallery API Response:", result);

        if (result.status) {
          const images = result.data || [];

          setGalleryData(images);

          // Reset pagination after loading data
          setCurrentPage(1);
        } else {
          console.error("Gallery API Error:", result.message);

          setGalleryData([]);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error("Gallery fetch error:", error);

        setGalleryData([]);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // ============================================================
  // KEEP CURRENT PAGE VALID
  // ============================================================

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ============================================================
  // IMAGE URL
  // ============================================================

  const getImageUrl = (image) => {
    if (!image) return "";

    // If PHP already returns a full URL
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    // Remove leading slash
    const cleanImage = image.replace(/^\/+/, "");

    return `${BASE_URL}/${cleanImage}`;
  };

  // ============================================================
  // OPEN LIGHTBOX
  // ============================================================

  const openLightbox = (image, index) => {
    /*
     * current page index -> global gallery index
     */
    const globalIndex = startIndex + index;

    setSelectedImage(image);
    setSelectedIndex(globalIndex);
  };

  // ============================================================
  // CLOSE LIGHTBOX
  // ============================================================

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  // ============================================================
  // PREVIOUS IMAGE
  // ============================================================

  const showPrevious = () => {
    if (galleryData.length <= 1) return;

    const previousIndex =
      selectedIndex <= 0 ? galleryData.length - 1 : selectedIndex - 1;

    setSelectedIndex(previousIndex);
    setSelectedImage(galleryData[previousIndex]);

    /*
     * If previous image is on another page,
     * change pagination page automatically.
     */
    const previousPage = Math.floor(previousIndex / itemsPerPage) + 1;

    if (previousPage !== currentPage) {
      setCurrentPage(previousPage);
    }
  };

  // ============================================================
  // NEXT IMAGE
  // ============================================================

  const showNext = () => {
    if (galleryData.length <= 1) return;

    const nextIndex =
      selectedIndex >= galleryData.length - 1 ? 0 : selectedIndex + 1;

    setSelectedIndex(nextIndex);
    setSelectedImage(galleryData[nextIndex]);

    /*
     * If next image is on another page,
     * change pagination page automatically.
     */
    const nextPage = Math.floor(nextIndex / itemsPerPage) + 1;

    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }
  };

  // ============================================================
  // KEYBOARD NAVIGATION
  // ============================================================

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only work when lightbox is open
      if (!selectedImage) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        showPrevious();
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        showNext();
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeLightbox();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, selectedIndex, galleryData, currentPage]);

  // ============================================================
  // CHANGE PAGE
  // ============================================================

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);

    // Scroll gallery section to top
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ============================================================
  // PREVIOUS PAGE
  // ============================================================

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  // ============================================================
  // NEXT PAGE
  // ============================================================

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <main className="gallery-page">
        <section className="contact-hero">
          <div className="contact-hero-overlay"></div>

          <div
            className="section-container contact-hero-content"
            data-aos="fade-up"
            data-aos-duration="1000"
          >
            <span
              className="section-label"
              data-aos="fade-down"
              data-aos-delay="100"
            >
              OUR GALLERY
            </span>

            <h1 data-aos="fade-up" data-aos-delay="200">
              Moments That
              <span> Define Us</span>
            </h1>

            <p data-aos="fade-up" data-aos-delay="350">
              Explore moments from our team, training sessions, client
              interactions, events and everyday work at Star Computers.
            </p>
          </div>
        </section>

        <section className="gallery-section">
          <div className="section-container">
            <div className="gallery-empty">
              <FaImages />

              <h3>Loading Gallery...</h3>

              <p>Please wait while we load the images.</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <main className="gallery-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>

        <div className="section-container contact-hero-content">
          <span className="section-label">OUR GALLERY</span>

          <h1>
            Moments That
            <span> Define Us</span>
          </h1>

          <p>
            Explore moments from our team, training sessions, client
            interactions, events and everyday work at Star Computers.
          </p>
        </div>
      </section>

      {/* =====================================================
          GALLERY
      ===================================================== */}

      <section className="gallery-section">
        <div className="section-container">
          <div
            className="section-heading gallery-heading"
            data-aos="fade-up"
            data-aos-duration="900"
          >
            <span className="section-label">STAR COMPUTERS</span>

            <h2>
              Explore Our
              <span> Gallery</span>
            </h2>

            <p>
              Take a look at our work environment, professional team, training
              programs and business activities.
            </p>
          </div>

          {/* =================================================
              IMAGE GRID
          ================================================= */}

          {galleryData.length > 0 ? (
            <>
              <div className="gallery-grid">
                {currentGalleryData.map((item, index) => (
                  <div
                    className="gallery-card"
                    key={item.id}
                    onClick={() => openLightbox(item, index)}
                    data-aos="fade-up"
                    data-aos-duration="700"
                    data-aos-delay={(index % 6) * 100}
                  >
                    <div className="gallery-image-wrapper">
                      <img
                        src={getImageUrl(item.image)}
                        alt={`Gallery ${item.id}`}
                        className="gallery-image"
                        onError={(e) => {
                          console.error(
                            "Image failed:",
                            getImageUrl(item.image),
                          );

                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* =================================================
                  PAGINATION
              ================================================= */}

              {totalPages > 1 && (
                <div className="gallery-pagination">
                  {/* PREVIOUS */}
                  <button
                    type="button"
                    className="pagination-btn pagination-prev"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <FaChevronLeft />
                  </button>

                  {/* PAGE NUMBERS */}
                  <div className="pagination-pages">
                    {Array.from(
                      { length: totalPages },
                      (_, index) => index + 1,
                    ).map((page) => (
                      <button
                        type="button"
                        key={page}
                        className={`pagination-page ${
                          currentPage === page ? "active" : ""
                        }`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* NEXT */}
                  <button
                    type="button"
                    className="pagination-btn pagination-next"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}

              {/* PAGINATION INFO */}
              {totalPages > 1 && (
                <div className="pagination-info">
                  Showing <strong>{startIndex + 1}</strong> -{" "}
                  <strong>
                    {Math.min(startIndex + itemsPerPage, galleryData.length)}
                  </strong>{" "}
                  of <strong>{galleryData.length}</strong> images
                </div>
              )}
            </>
          ) : (
            <div className="gallery-empty">
              <FaImages />

              <h3>No Images Found</h3>

              <p>There are currently no images available in the gallery.</p>
            </div>
          )}
        </div>
      </section>

      {/* =====================================================
          LIGHTBOX
      ===================================================== */}

      {selectedImage && (
        <div className="gallery-lightbox" onClick={closeLightbox}>
          {/* CLOSE */}
          <button
            type="button"
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Close image"
          >
            <FaTimes />
          </button>

          {/* PREVIOUS */}
          {galleryData.length > 1 && (
            <button
              type="button"
              className="lightbox-arrow lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrevious();
              }}
              aria-label="Previous image"
            >
              <FaChevronLeft />
            </button>
          )}

          {/* IMAGE */}
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getImageUrl(selectedImage.image)}
              alt={`Gallery ${selectedImage.id}`}
            />
          </div>

          {/* NEXT */}
          {galleryData.length > 1 && (
            <button
              type="button"
              className="lightbox-arrow lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              aria-label="Next image"
            >
              <FaChevronRight />
            </button>
          )}
        </div>
      )}
    </main>
  );
}
